import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Check, Plus, Users } from 'lucide-react'
import {
  getOrganization,
  subscribeToOrgMembers,
  getOrgSessions,
  type SessionWithId,
} from '../../lib/organization'
import type { Organization, OrgMember } from '../../types'
import OrgSessionCard from './OrgSessionCard'

export default function OrgDashboardPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [org, setOrg] = useState<Organization | null>(null)
  const [members, setMembers] = useState<(OrgMember & { uid: string })[]>([])
  const [sessions, setSessions] = useState<SessionWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!orgId) return
    getOrganization(orgId).then(o => {
      setOrg(o)
      setLoading(false)
    })
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    return subscribeToOrgMembers(orgId, setMembers)
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    getOrgSessions(orgId).then(setSessions).catch((err) => console.error('Failed to load team sessions:', err))
  }, [orgId])

  function handleCopyInvite() {
    if (!org) return
    navigator.clipboard.writeText(`${window.location.origin}/org/join/${org.inviteCode}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <p className="mono-label text-navy/70">
          Loading the team
          <span className="caret" aria-hidden="true" />
        </p>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="hl text-2xl text-navy">Team not found</h1>
          <button onClick={() => navigate('/')} className="text-sm text-amber hover:text-amber-dark font-semibold">
            Back to the homepage
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone">
      {/* Header */}
      <header className="bg-navy text-stone px-6 py-4 border-b-2 border-golden">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-1.5 hover:bg-white/10 transition-colors cursor-pointer">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="hl text-xl text-stone">{org.name}</h1>
              <p className="text-xs text-stone/70">{members.length} member{members.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopyInvite}
              className="flex items-center gap-1.5 mono-label border-2 border-stone/55 px-3 py-2 hover:bg-white/10 transition cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Invite link'}
            </button>
            <button
              onClick={() => navigate(`/?org=${orgId}`)}
              className="flex items-center gap-1.5 mono-label bg-amber hover:bg-amber-dark text-white border-2 border-amber px-3 py-2 transition cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              New session
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Members Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-navy/70" />
            <h2 className="mono-label text-teal">Members</h2>
          </div>
          <div className="card card-shadow divide-y divide-navy/15">
            {members.map(m => (
              <div key={m.uid} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center text-sm font-semibold text-navy">
                    {m.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-navy">{m.displayName}</p>
                    <p className="text-xs text-navy/70">{m.email}</p>
                  </div>
                </div>
                <span className={`mono-label border px-1.5 py-0.5 ${
                  m.role === 'owner' ? 'border-amber text-amber-dark' : 'border-navy/55 text-navy/72'
                }`}>
                  {m.role}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Invite code */}
        <section className="card card-shadow p-5">
          <p className="text-xs text-navy/70 mb-2">Invite code</p>
          <div className="flex items-center gap-3">
            <span className="text-lg font-mono font-bold tracking-widest text-navy">{org.inviteCode}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(org.inviteCode)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="p-1 rounded hover:bg-paper transition"
            >
              {copied ? <Check className="w-4 h-4 text-teal" /> : <Copy className="w-4 h-4 text-navy/70" />}
            </button>
          </div>
          <p className="text-[11px] text-navy/70 mt-1">Share this code or the invite link so others can join your team</p>
        </section>

        {/* Sessions Section */}
        <section>
          <h2 className="mono-label text-teal mb-4">Sessions</h2>
          {sessions.length === 0 ? (
            <div className="card card-shadow p-8 text-center">
              <p className="text-sm text-navy/70 mb-4">No sessions yet</p>
              <button
                onClick={() => navigate(`/?org=${orgId}`)}
                className="inline-flex items-center gap-1.5 bg-amber hover:bg-amber-dark text-white font-semibold px-6 py-2.5 text-sm transition"
              >
                <Plus className="w-4 h-4" />
                Start First Session
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sessions.map(s => (
                <OrgSessionCard key={s.id} session={s} orgId={orgId!} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
